/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  IconButton,
  Box,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WaterScheduleBuilder from './WaterScheduleBuilder';
import { getNestedValue, isFieldVisible, setNestedValue } from './schemaUtils';

/**
 * Picks a compartment the project currently occupies.
 *
 * `selectBy` decides what one option means:
 *
 *   'assignment' (default) — one option per active assignment. A cart request
 *       modifies a specific assignment's quantity, so it has to name one.
 *   'chamber' — one option per compartment, however many assignments it holds.
 *       Environmental conditions and watering apply to the compartment itself,
 *       so listing the same compartment three times because it has three rate
 *       assignments would ask the client a question that has no meaning.
 */
const ChamberAssignmentSelect = ({ field, value, onChange, disabled, project }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const byChamber = field.selectBy === 'chamber';

  useEffect(() => {
    let cancelled = false;
    if (!project?._id) return undefined;
    setLoading(true);
    axios.get(`/api/projects/${project._id}/assignments`)
      .then(({ data }) => {
        if (cancelled) return;
        const flat = [];
        (data.assignments || []).forEach((ca) => {
          const chamberName = ca.chamber?.chamber || ca.chamber?.description || 'Compartment';
          const active = (ca.assignments || []).filter(a => a.status === 'Active');
          if (active.length === 0) return;

          // Carried so fields further down the form (the irrigation schedule)
          // can offer only what this compartment's facility actually supplies.
          const facility = {
            facility_id: ca.chamber?.facility?._id || ca.chamber?.facility || null,
            facility_name: ca.chamber?.facility?.facility_name || ''
          };
          const unitShort = ca.chamber?.capacity?.unit?.unit_short || '';

          if (byChamber) {
            // One entry per compartment, with its active assignments rolled
            // up. The ids are kept so the choice can still be traced back to
            // what the compartment held at the time.
            const earliest = active
              .map(a => a.start_date)
              .filter(Boolean)
              .sort((x, y) => new Date(x) - new Date(y))[0] || null;
            flat.push({
              id: `chamber:${ca.chamber?._id}`,
              scope: 'chamber',
              chamber_id: ca.chamber?._id,
              chamber_name: chamberName,
              assignment_ids: active.map(a => String(a._id)),
              assignment_count: active.length,
              quantity: active.reduce((sum, a) => sum + (Number(a.quantity) || 0), 0),
              start_date: earliest,
              unit_short: unitShort,
              ...facility
            });
            return;
          }

          active.forEach((a) => {
            flat.push({
              id: String(a._id),
              scope: 'assignment',
              chamber_id: ca.chamber?._id,
              chamber_name: chamberName,
              rate_id: a.rate_id,
              quantity: a.quantity,
              start_date: a.start_date,
              end_date: a.end_date,
              unit_short: unitShort,
              ...facility
            });
          });
        });
        setOptions(flat);
      })
      .catch((err) => {
        console.error('Failed to load chamber assignments:', err);
        if (!cancelled) setError('Failed to load active assignments');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project?._id, byChamber]);

  const selectedId = value?.id || '';

  const handleChange = (e) => {
    const id = e.target.value;
    const opt = options.find(o => o.id === id);
    onChange(field.name, opt ? { ...opt } : null);
  };

  const formatLabel = (o) => {
    if (o.scope === 'chamber') {
      const parts = [o.chamber_name];
      if (o.quantity) parts.push(`${o.quantity} ${o.unit_short || 'units'}`);
      if (o.assignment_count > 1) parts.push(`${o.assignment_count} assignments`);
      if (o.start_date) parts.push(`since ${new Date(o.start_date).toLocaleDateString()}`);
      return parts.join(' · ');
    }
    return `${o.chamber_name} · ${o.rate_id} · ${o.quantity} ${o.unit_short || 'units'}`
      + (o.start_date ? ` · since ${new Date(o.start_date).toLocaleDateString()}` : '');
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          {byChamber ? 'Loading compartments…' : 'Loading active assignments…'}
        </Typography>
      </Stack>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  if (options.length === 0) {
    return (
      <Alert severity="info">
        {byChamber
          ? 'This project has no compartments assigned at the moment.'
          : 'No active compartment assignments found for this project.'}
      </Alert>
    );
  }

  return (
    <FormControl fullWidth required={field.required} disabled={disabled}>
      <InputLabel>{field.label}</InputLabel>
      <Select
        value={selectedId}
        label={field.label}
        onChange={handleChange}
      >
        {options.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {formatLabel(o)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const FieldInput = ({ field, value, onChange, disabled, project, facilityId }) => {
  const handle = (v) => onChange(field.name, v);
  const readOnly = Boolean(field.readonly);
  const inputDisabled = disabled || readOnly;

  switch (field.type) {
    case 'chamberAssignmentSelect':
      return (
        <ChamberAssignmentSelect
          field={field}
          value={value}
          onChange={onChange}
          disabled={inputDisabled}
          project={project}
        />
      );
    case 'waterScheduleBuilder':
      return (
        <WaterScheduleBuilder
          field={field}
          value={value}
          onChange={onChange}
          disabled={inputDisabled}
          facilityId={facilityId}
        />
      );
    case 'text':
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          required={field.required}
          helperText={field.helper}
          disabled={inputDisabled}
        />
      );
    case 'textarea':
      return (
        <TextField
          fullWidth
          multiline
          rows={field.rows || 3}
          label={field.label}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          required={field.required}
          helperText={field.helper}
          disabled={inputDisabled}
        />
      );
    case 'number':
      return (
        <TextField
          fullWidth
          type="number"
          label={field.label}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          required={field.required}
          helperText={field.helper}
          disabled={inputDisabled}
          inputProps={{ min: field.min, max: field.max, step: field.step }}
        />
      );
    case 'date':
      return (
        <TextField
          fullWidth
          type="date"
          label={field.label}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          InputLabelProps={{ shrink: true }}
          required={field.required}
          helperText={field.helper}
          disabled={inputDisabled}
        />
      );
    case 'time':
      return (
        <TextField
          fullWidth
          type="time"
          label={field.label}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          InputLabelProps={{ shrink: true }}
          required={field.required}
          helperText={field.helper}
          disabled={inputDisabled}
        />
      );
    case 'datetimeAmPm': {
      const v = value && typeof value === 'object' ? value : {};
      const update = (patch) => handle({ ...v, ...patch });
      const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
      const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
      return (
        <Box>
          {field.label && (
            <FormLabel
              component="legend"
              required={field.required}
              sx={{ fontSize: '0.85rem', mb: 0.75 }}
            >
              {field.label}
            </FormLabel>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              type="date"
              label="Date"
              value={v.date || ''}
              onChange={(e) => update({ date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required={field.required}
              disabled={inputDisabled}
              sx={{ flex: 2 }}
            />
            <FormControl required={field.required} disabled={inputDisabled} sx={{ flex: 1, minWidth: 90 }}>
              <InputLabel>Hour</InputLabel>
              <Select
                label="Hour"
                value={v.hour || ''}
                onChange={(e) => update({ hour: e.target.value })}
              >
                {hours.map((h) => (
                  <MenuItem key={h} value={h}>{h}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl required={field.required} disabled={inputDisabled} sx={{ flex: 1, minWidth: 90 }}>
              <InputLabel>Minute</InputLabel>
              <Select
                label="Minute"
                value={v.minute || ''}
                onChange={(e) => update({ minute: e.target.value })}
              >
                {minutes.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl required={field.required} disabled={inputDisabled} sx={{ flex: 1, minWidth: 90 }}>
              <InputLabel>AM/PM</InputLabel>
              <Select
                label="AM/PM"
                value={v.period || ''}
                onChange={(e) => update({ period: e.target.value })}
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          {field.helper && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {field.helper}
            </Typography>
          )}
        </Box>
      );
    }
    case 'select':
      return (
        <FormControl fullWidth required={field.required} disabled={inputDisabled}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value ?? ''}
            label={field.label}
            onChange={(e) => handle(e.target.value)}
          >
            {(field.options || []).map((opt) => (
              <MenuItem key={String(opt.value)} value={opt.value}>
                {opt.label || opt.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case 'radio':
      return (
        <FormControl component="fieldset" disabled={inputDisabled}>
          <FormLabel component="legend">{field.label}</FormLabel>
          <RadioGroup
            row={field.row !== false}
            value={value ?? ''}
            onChange={(e) => handle(e.target.value)}
          >
            {(field.options || []).map((opt) => (
              <FormControlLabel
                key={String(opt.value)}
                value={opt.value}
                control={<Radio />}
                label={opt.label || opt.value}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    case 'checkbox':
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => handle(e.target.checked)}
              disabled={inputDisabled}
            />
          }
          label={field.label}
        />
      );
    case 'switch':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => handle(e.target.checked)}
              disabled={inputDisabled}
            />
          }
          label={field.label}
        />
      );
    default:
      return (
        <TextField
          fullWidth
          label={field.label}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          helperText={field.helper}
          disabled={inputDisabled}
        />
      );
  }
};

const RepeaterField = ({ field, value, onChange, disabled, formData }) => {
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    const empty = {};
    (field.fields || []).forEach((sub) => { empty[sub.name] = sub.defaultValue ?? ''; });
    onChange(field.name, [...items, empty]);
  };
  const removeItem = (idx) => {
    onChange(field.name, items.filter((_, i) => i !== idx));
  };
  const updateItem = (idx, subName, subVal) => {
    const next = items.map((it, i) => (i === idx ? setNestedValue(it, subName, subVal) : it));
    onChange(field.name, next);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2">{field.label}</Typography>
        {field.required && <Typography color="error" variant="caption">*</Typography>}
        <IconButton aria-label="Add" size="small" onClick={addItem} disabled={disabled}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No items. Click + to add a {field.itemLabel || 'row'}.
        </Typography>
      )}
      <Stack spacing={2}>
        {items.map((item, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {field.itemLabel || 'Item'} #{idx + 1}
              </Typography>
              <IconButton aria-label="Delete" size="small" onClick={() => removeItem(idx)} disabled={disabled}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack spacing={2}>
              {(field.fields || []).map((sub) => {
                if (!isFieldVisible(sub, item)) return null;
                return (
                  <FieldInput
                    key={sub.name}
                    field={sub}
                    value={getNestedValue(item, sub.name)}
                    onChange={(name, val) => updateItem(idx, name, val)}
                    disabled={disabled}
                  />
                );
              })}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

const SchemaField = ({ field, formData, setFormData, disabled, project, facilityId }) => {
  if (!isFieldVisible(field, formData)) return null;

  const value = getNestedValue(formData, field.name);
  const onChange = (name, val) => {
    setFormData((prev) => setNestedValue(prev, name, val));
  };

  if (field.type === 'repeater') {
    return (
      <RepeaterField
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        formData={formData}
      />
    );
  }

  return (
    <FieldInput
      field={field}
      value={value}
      onChange={onChange}
      disabled={disabled}
      project={project}
      facilityId={facilityId}
    />
  );
};

export default SchemaField;
